/* Deskon - the hero button gets a discount pill and an offer block beside it.

     .hero-cta
       .hero-cta__btn-wrap    the moved button + the overhanging pill
       .hero-cta__rule        a white vertical bar
       .hero-cta__offer       two lines, uppercased by CSS

   SCRIPT AND NOT LIQUID: the button is rendered by sections/hero.liquid, 68 KB, and the Theme API
   takes whole files - wrapping it at the source means resending all of it for a handful of tags.

   THE WRAP IS A MOVE, NOT A COPY, so the button's classes, href and label survive untouched. It
   also costs the button its place in the cascade: every rule for it targets the promo group's
   fourth child, so once .hero-cta occupies that slot the ROW receives all of it - background,
   height, hover, and the label's font-size on every descendant. assets/deskon-hero-cta-offer.css
   exists to undo that. Read it before changing anything here.

   BLOCKS ARE FOUND BY NATURE, NOT BY POSITION. This used to read group.children[2] and [3], which
   assumed all four promo blocks render. Disabling either of the two middle blocks in the editor
   shifted every index, children[3] came back undefined, and the script gave up silently - no row,
   no rule, no pill, and a bare button left in place. There is nothing in the editor to warn about
   that, and a disabled block nested in a static group does not always appear in the sidebar, so
   the cause is invisible from both ends.

     the button  the LAST child holding an <a> or <button>; it is last in block_order, and taking
                 the last one keeps a link elsewhere in the group from winning
     the offer   the LAST group block before the button - discount_stack. It used to be the
                 child whose text contained "until", which meant any rewording without that
                 word was silently ignored and the fallback text shown instead. sale_stack is
                 also a group, but it sits before discount_stack in block_order, so taking the
                 last group keeps it from winning

   THE OFFER BLOCK IS OPTIONAL NOW. When it is disabled the fallbacks below supply the two
   lines, and the row renders exactly as before. The figure and the wording then come
   from this file rather than the editor, which is the trade for surviving a disabled block.

   THE TEXT IS THE EDITOR'S, AS TYPED. The last text block of the offer group is shown verbatim,
   on two lines:
     - a line break typed in the editor (Shift+Enter, or a second paragraph) decides the split;
     - otherwise, if the text contains "until", it splits just before that word;
     - otherwise it splits between words where the two lines come out closest in length.

   THE DEADLINE IS COMPUTED ONLY ON REQUEST. If the text ENDS with the bare word "until", the
   date <today + OFFER_DAYS_AHEAD> is appended on every page load. Any date typed after "until"
   is shown as typed - it is no longer overwritten.

   IT COMES FROM THE VISITOR'S CLOCK, unavoidably. Rendering it in Liquid would freeze it in
   Shopify's CDN cache and it could sit a day behind for everyone. A device set to the wrong day
   shows the wrong date and nothing here can detect that.

   setDate() HANDLES THE ROLLOVER: 30 August plus two is 1 September, 30 December plus two is
   1 January of the next year. No arithmetic here to get wrong.

   THE FORMAT IS en-GB, NOT THE BROWSER'S LOCALE. en-GB gives "23 August", the British order for a
   British shop; en-US would give "August 23" and a French browser "23 aout". The rest of the line
   is English, so the visitor's locale is the wrong input.

   When splitting on "until", the trailing comma before it is stripped, since the two lines are
   stacked phrases and a comma before a break has nothing to join.

   THE LINE BREAK NEEDS CSS HELP when the offer group is the only middle block enabled: the row is
   then the group's THIRD child, and deskon-hero-type.css / deskon-hero-offer-text.css hide every
   <br> in the third child (a rule written for the old offer text). deskon-hero-cta-width.css puts
   the <br> back for .hero-cta__offer.

   IDEMPOTENT via the wrapper it would create, checked on the element - the theme editor replaces
   markup wholesale, and a flag in memory would outlive the elements it described. It retries
   briefly because the button is a theme block whose upgrade time varies. */

const WRAPPER = '.hero-split-wrapper';
const GROUP = '.hero-split-wrapper__promo .color-scheme-deskon2-ink > .group-block-content';

const OFFER_DAYS_AHEAD = 2;

/* AUTUMN SALE (16/10/2026, user request). While SALE_LINE_1 is not empty, the offer block shows
   exactly two lines - SALE_LINE_1 and "until <date>" - and the editor's text block is ignored.
   The date is UK style, day first, month abbreviated ("18 Oct", shown "UNTIL 18 OCT" by the CSS
   uppercase) - harmonised with the menu and empty cart on 16/10/2026. The months are a fixed list
   rather than toLocaleDateString, so every browser writes them the same way (some write "Sept").
   Set SALE_LINE_1 to '' to go back to the editor's text, and SALE_PILL to '' to remove the
   pill on the button. */
const SALE_LINE_1 = 'Autumn sale';
const SALE_PILL = '−30%';
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const FALLBACK_LINE_1 = 'On all lamps';
const FALLBACK_KEYWORD = 'until';

const RETRY_DELAYS = [0, 80, 200, 500, 1200];

function textOf(element) {
  if (!element) return '';
  return element.textContent.replace(/\s+/g, ' ').trim();
}

function deadline() {
  const date = new Date();
  date.setDate(date.getDate() + OFFER_DAYS_AHEAD);

  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}`;
}

/* The last child that IS or holds a link or a button, skipping a row this script already built.
   The button block renders as a bare <a> sitting directly in the group, and querySelector only
   searches descendants - it never matches the element itself, so the check has to test the child
   with matches() as well. Without it the <a> was skipped and the row was never built. */
function findCta(group) {
  const children = Array.from(group.children);

  for (let index = children.length - 1; index >= 0; index -= 1) {
    const child = children[index];
    if (child.classList.contains('hero-cta')) continue;
    if (child.matches('a, button') || child.querySelector('a, button')) return child;
  }

  return null;
}

/* The last group block before the button: discount_stack (see the header). */
function findOffer(group, cta) {
  const groups = Array.from(group.children).filter(
    (child) =>
      child !== cta &&
      !child.classList.contains('hero-cta') &&
      child.classList.contains('group-block')
  );

  return groups.length ? groups[groups.length - 1] : null;
}

/* The lines of a rich-text block, keeping the breaks typed in the editor. */
function linesOf(element) {
  if (!element) return [];

  const copy = element.cloneNode(true);
  copy.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));

  const paragraphs = copy.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6');
  const text = paragraphs.length
    ? Array.from(paragraphs).map((p) => p.textContent).join('\n')
    : copy.textContent;

  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/* Two lines of close length, split between words. */
function balance(sentence) {
  const words = sentence.split(' ');
  if (words.length < 2) return [sentence];

  let best = 1;
  let bestGap = Infinity;

  for (let index = 1; index < words.length; index += 1) {
    const gap = Math.abs(words.slice(0, index).join(' ').length - words.slice(index).join(' ').length);
    if (gap < bestGap) {
      bestGap = gap;
      best = index;
    }
  }

  return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
}

/* The offer text as it will be shown, one entry per line. */
function offerLines(element) {
  if (SALE_LINE_1) return [SALE_LINE_1, `${FALLBACK_KEYWORD} ${deadline()}`];

  let lines = linesOf(element);
  if (!lines.length) return [FALLBACK_LINE_1, `${FALLBACK_KEYWORD} ${deadline()}`];

  if (lines.length === 1) {
    const match = lines[0].match(/^(.*?)\s*\b(until)\b\s*(.*)$/i);
    const lead = match ? match[1].trim().replace(/[,;:]+$/, '') : '';

    lines = lead ? [lead, match[3] ? `${match[2]} ${match[3]}` : match[2]] : balance(lines[0]);
  }

  const last = lines.length - 1;
  if (/\buntil$/i.test(lines[last])) lines[last] = `${lines[last]} ${deadline()}`;

  return lines;
}

function build() {
  const group = document.querySelector(GROUP);
  if (!group) return false;
  if (group.querySelector('.hero-cta')) return true;

  const cta = findCta(group);
  if (!cta) return false;

  const offerBlock = findOffer(group, cta);
  const offerInner = offerBlock ? offerBlock.querySelector('.group-block-content') || offerBlock : null;

  const textBlock = offerInner && offerInner.children.length ? offerInner.lastElementChild : null;
  const lines = offerLines(textBlock);

  const row = document.createElement('div');
  row.className = 'hero-cta';

  const wrap = document.createElement('div');
  wrap.className = 'hero-cta__btn-wrap';

  cta.parentNode.insertBefore(row, cta);
  wrap.appendChild(cta);

  /* The discount pill, back for the Autumn sale (16/10/2026). Positioned and styled by
     assets/deskon-hero-cta-offer.css (.hero-cta__pill): top-right corner of the button. */
  if (SALE_PILL) {
    const pill = document.createElement('span');
    pill.className = 'hero-cta__pill';
    pill.textContent = SALE_PILL;
    wrap.appendChild(pill);
  }

  const rule = document.createElement('span');
  rule.className = 'hero-cta__rule';
  rule.setAttribute('aria-hidden', 'true');

  const offer = document.createElement('p');
  offer.className = 'hero-cta__offer';

  lines.forEach((line, index) => {
    if (index) offer.appendChild(document.createElement('br'));
    const span = document.createElement('span');
    span.textContent = line;
    offer.appendChild(span);
  });

  row.appendChild(wrap);
  row.appendChild(rule);
  row.appendChild(offer);

  if (offerBlock) offerBlock.style.display = 'none';

  const wrapper = document.querySelector(WRAPPER);
  if (wrapper) wrapper.classList.add('hero-has-cta-row');

  return true;
}

let timers = [];

function run() {
  timers.forEach(clearTimeout);
  timers = [];

  for (const delay of RETRY_DELAYS) {
    timers.push(
      setTimeout(() => {
        if (build()) {
          timers.forEach(clearTimeout);
          timers = [];
        }
      }, delay)
    );
  }
}

run();

window.addEventListener('load', run);
document.addEventListener('shopify:section:load', run);